/**
 * GIS Services AppConfig.
 *
 * GIS Services does NOT mount the shared Map / Table / Dashboard shells —
 * the route renders its own catalog landing page and per-layer detail pages.
 * The config exists so the app is listed alongside its peers and the shared
 * Header reads brand + nav metadata from a consistent place.
 *
 * Operational fields used by the shared shells (layers, legends, columns,
 * widgets, filters, attributes) are left empty: nothing in this app routes
 * through them.
 */

import { goto } from '$app/navigation';
import { resolve } from '$app/paths';
import type { AppConfig } from '#lib/apps/types.js';
import LogoGISServices from '#lib/assets/logos/gis-services.svg?raw';
import type { IGisServicesSystemData } from './interfaces';
import { loadSystem } from './loaders';
import { platformAppVersion } from '#lib/config.js';

export const gisServicesConfig: AppConfig<IGisServicesSystemData> = {
	id: 'gis-services',
	name: 'GIS Services',
	basePath: '/gis-services',
	head: {
		title: 'Abley GIS Services',
		description: 'Abley Spatial platform GIS Services explorer - link into GIS applications.',
		keywords: 'Spatial, Services, GIS, ArcGIS, QGIS, Enterprise, Platform, WFS, WMS, Abley Ltd.'
	},
	logo: LogoGISServices,
	// 'full' so MapTiler's own roads show — PreviewMap doesn't overlay the
	// GeoServer context-roads layer the shared Map shell uses for 'no-roads' apps.
	basemap: 'full',
	version: platformAppVersion,
	// GIS Services has no GeoServer dataset of its own — availability is gated at
	// runtime on its WFS/WMS layer counts (see `gisServicesLayersStore`), fed to
	// `getAppsWithAvailability` as a runtime override rather than via config here.
	datasets: [],

	loaders: {
		system: loadSystem
	},

	legends: [],

	table: {
		tabs: [],
		defaultTabId: ''
	},

	filters: [],

	dashboard: {
		widgets: []
	},

	header: {
		dashboard: {
			label: 'Home',
			action: () => void goto(resolve('gis-services'))
		}
	},

	attributes: {
		panel: null,
		countermeasures: null
	}
};
